import { useState, useCallback } from 'react';
import { personalActionsAPI } from '../api/personal-actions';

export const usePersonalActions = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadActions = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.getAll(filters);
      if (data.success) {
        setActions(data.actions || []);
        return { success: true, actions: data.actions };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar acciones';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (employeeId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.getEmployeeHistory(employeeId);
      if (data.success) {
        return { success: true, actions: data.actions };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar historial';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const createAction = useCallback(async (actionData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.create(actionData);
      if (data.success) {
        return { success: true, action: data.action };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al crear acción';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAction = useCallback(async (id, actionData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.update(id, actionData);
      if (data.success) {
        return { success: true, action: data.action };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al actualizar acción';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAction = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.delete(id, motivo);
      if (data.success) {
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al revocar acción';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const approveAction = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.approve(id);
      if (data.success) {
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al aprobar acción';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectAction = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await personalActionsAPI.reject(id, motivo);
      if (data.success) {
        return { success: true };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al rechazar acción';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await personalActionsAPI.getStats();
      if (data.success) {
        return { success: true, stats: data.stats };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar estadísticas';
      return { success: false, error: errorMsg };
    }
  }, []);

  const loadTipos = useCallback(async () => {
    try {
      const data = await personalActionsAPI.getTipos();
      if (data.success) {
        return { success: true, tipos: data.tipos };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar tipos';
      return { success: false, error: errorMsg };
    }
  }, []);

  return {
    actions,
    loading,
    error,
    loadActions,
    loadHistory,
    createAction,
    updateAction,
    deleteAction,
    approveAction,
    rejectAction,
    loadStats,
    loadTipos
  };
};

export default usePersonalActions;
